import { AppContext, BookingData } from "../../../../AppDataProvider/AppDataProvider";
import { Button as BaseButton, LoadingSpinner, Notification } from "../../../../common/components";
import { BookingVisibilityType, useCreateBookingsWithStripeInvoiceMutation, useCreateStripePaymentIntentMutation } from "../../../../GeneratedGraphQL";
import { getBookingQuestionInput, getCrmFieldBookingInput, requireBookingDataFields } from "../../../../utils";
import { marginTop, media, padding } from "../../../../common/styles";
import React, { useContext, useState } from "react";
import { StripeCardElement, StripeCardElementChangeEvent } from "@stripe/stripe-js";
import styled, { useTheme } from "styled-components";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import { ActionType } from "../../../../AppDataProvider/actions";
import { CardElement } from "@stripe/react-stripe-js";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import i18n from "../../../../i18n";
import { Mixpanel } from "../../../../mixpanel";
import { PartRequired } from "../../../../utils/types";
import { useTranslation } from "react-i18next";
export type StripePaymentFormProps = {
  onGoBack: () => void;
  onPayAndBook: (bookingIds: string[]) => void;
  loading: boolean;
};
export function StripePaymentForm(props: StripePaymentFormProps): React.ReactElement {
  const {
    t
  } = useTranslation();
  const theme = useTheme();
  const {
    onGoBack,
    onPayAndBook,
    loading
  } = props;
  const [succeeded, setSucceeded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [invalidCard, setInvalidCard] = useState(false);
  const stripe = useStripe();
  const elements = useElements();
  const {
    appData,
    appDataDispatch
  } = useContext(AppContext);
  type PaymentsStepBookingData = PartRequired<BookingData, "createdSource" | "organisation" | "reservedSpaceId" | "service" | "timezone" | "userDetails">;
  requireBookingDataFields<PaymentsStepBookingData>(appData.bookingData, ["createdSource", "organisation", "service", "userDetails", "reservedSpaceId", "timezone"]);
  const {
    createdSource,
    organisation,
    userDetails,
    reservedSpaceId,
    timezone,
    discountCode,
    tier,
    pricePoints,
    bookingNotes,
    service,
    bookingQuestionData
  } = appData.bookingData;
  const {
    crmFieldData
  } = userDetails;
  const selectedBookings = appData.bookingData.selectedBookings;
  const confirmPaymentLabel = service.bookingVisibilityType === BookingVisibilityType.Request ? t("requestAndPay") : t("confirmAndPay");
  const [createStripePaymentIntentInput] = useCreateStripePaymentIntentMutation({
    errorPolicy: "all"
  });
  const [createBookingsWithStripeInvoice] = useCreateBookingsWithStripeInvoiceMutation({
    errorPolicy: "all"
  });
  if (loading) {
    return <> </>;
  }
  if (!stripe || !elements) {
    return <ErrorNotification variant="warning" role="alert">
        {t("stripe.errors.loadingPaymentFormError")}
      </ErrorNotification>;
  }

  /**
   * This function is intended to capture a stripe payment
   * intent and create associated bookings in one action.
   *
   * 1. Create a payment method using card details.
   * 2. Create a payment intent using the card method.
   * 3. Handle any 3DS authentication using the secret returned by the intent.
   * 4. Use the confirmed card payment intent id to authorise the payment.
   *
   * Customer facing error messages are specific for the time being
   * and will likely need re-visited when we are implementing
   * high fidelity styles & content.
   */
  const createBookingsAndCapturePayment = async () => {
    setProcessing(true);

    /**
     * Type casting to a StripeCardElement as we check if it exists
     * before we call this function.
     */
    const cardElement = (elements.getElement(CardElement) as StripeCardElement);
    const createPaymentMethodResult = await stripe.createPaymentMethod({
      card: cardElement,
      type: "card"
    });
    const {
      paymentMethod,
      error: paymentMethodError
    } = createPaymentMethodResult;
    if (!paymentMethod) {
      console.error({
        paymentMethodError
      });
      setProcessing(false);
      setSucceeded(false);
      setError(t("stripe.errors.paymentMethodError"));
      Mixpanel.track({
        name: "Payment failed",
        props: {
          provider: "STRIPE",
          reason: "Failed to create payment method"
        }
      });
      return;
    }
    const bookings = [{
      ...(bookingNotes && {
        bookingNotes
      }),
      bookingQuestions: getBookingQuestionInput({
        bookingQuestionData,
        bookingQuestions: organisation.externalBookingQuestions,
        pickedServiceId: service._id
      }),
      customer: {
        ...userDetails,
        crmFieldData: getCrmFieldBookingInput(crmFieldData)
      },
      organisationId: organisation._id,
      reservedSpaceId: reservedSpaceId,
      timezone: timezone,
      ...(selectedBookings && {
        bookingIds: selectedBookings.map(booking => booking._id)
      }),
      ...(pricePoints && {
        pricePoints: pricePoints.map(pricePoint => {
          return {
            pricePointId: pricePoint.pricePoint._id,
            quantity: pricePoint.quantity
          };
        })
      }),
      ...(tier && {
        tierId: tier._id
      })
    }];
    const {
      data: createPaymentIntentData,
      errors: createPaymentIntentErrors
    } = await createStripePaymentIntentInput({
      variables: {
        input: {
          bookings,
          organisationId: organisation._id,
          ...(discountCode && {
            discount: {
              customerEmail: userDetails.email,
              discountCode
            }
          }),
          paymentMethodId: paymentMethod.id
        }
      }
    });
    if (!createPaymentIntentData?.createStripePaymentIntent.clientSecret) {
      if (createPaymentIntentErrors?.some(error => error.extensions?.code === "BAD_USER_INPUT" && error.extensions?.appointeddErrorCode !== "CARD_PAYMENT_ERROR")) {
        appDataDispatch({
          payload: {
            buttonLabel: t("reload"),
            content: t("errors.somethingChanged"),
            icon: faExclamationTriangle,
            onClick: /* istanbul ignore next */() => location.reload(),
            title: t("somethingChanged")
          },
          type: ActionType.SHOW_MODAL_DIALOG
        });
      }
      console.error({
        createPaymentIntentErrors
      });
      setError(t("stripe.errors.paymentIntentError"));
      Mixpanel.track({
        name: "Payment failed",
        props: {
          provider: "STRIPE",
          reason: "Failed to create payment intent"
        }
      });
      return;
    }
    const {
      clientSecret
    } = createPaymentIntentData.createStripePaymentIntent;
    const handleCardPaymentResult = await stripe.confirmCardPayment(clientSecret);
    const {
      paymentIntent,
      error: handleCardPaymentResultError
    } = handleCardPaymentResult;
    if (!paymentIntent) {
      console.error({
        handleCardPaymentResultError
      });
      setError(t("stripe.errors.confirmCardPaymentError"));
      Mixpanel.track({
        name: "Payment failed",
        props: {
          provider: "STRIPE",
          reason: "Failed to confirm card payment"
        }
      });
      return;
    }

    // Ignore lack of test coverage for locale "cimode" as it will only have "cimode" when in test mode
    /* istanbul ignore next */
    const locale = i18n.language !== "cimode" ? i18n.language : "en-GB";
    const {
      data: createdBookings,
      errors: createBookingsResultErrors
    } = await createBookingsWithStripeInvoice({
      variables: {
        bookings,
        ...(!appData.bookingTool.isGenericBookingTool && {
          bookingToolId: appData.bookingTool._id
        }),
        ...(discountCode && {
          discount: {
            customerEmail: userDetails.email,
            discountCode
          }
        }),
        cardToken: paymentIntent.id,
        createdSource,
        locale
      }
    });
    if (!createdBookings) {
      if (createBookingsResultErrors?.some(error => error.extensions?.code === "BAD_USER_INPUT")) {
        appDataDispatch({
          payload: {
            buttonLabel: t("reload"),
            content: t("errors.somethingChanged"),
            icon: faExclamationTriangle,
            onClick: /* istanbul ignore next */() => location.reload(),
            title: t("somethingChanged")
          },
          type: ActionType.SHOW_MODAL_DIALOG
        });
      }
      console.error({
        createBookingsResultErrors
      });
      setError(t("stripe.errors.confirmBookingError"));
      Mixpanel.track({
        name: "Payment failed",
        props: {
          provider: "STRIPE",
          reason: "Error during invoice payment and booking creation"
        }
      });
      return;
    }

    // Booking has been paid for & confirmed
    Mixpanel.track({
      name: "Payment successful",
      props: {
        provider: "STRIPE"
      }
    });
    onPayAndBook(createdBookings.createBookingsWithStripeInvoice.bookings.map(booking => booking._id));
  };
  const handleSubmit = (event: React.SyntheticEvent) => {
    // Prevent default form submission page refresh
    event.preventDefault();
    if (invalidCard) return;
    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError(t("stripe.errors.getCardElementError"));
      Mixpanel.track({
        name: "Payment failed",
        props: {
          provider: "STRIPE",
          reason: "Error getting card details"
        }
      });
      return;
    }
    createBookingsAndCapturePayment().catch(error => {
      setProcessing(false);
      setSucceeded(false);
      console.error(error);
      setError(t("stripe.errors.confirmBookingError"));
      Mixpanel.track({
        name: "Payment failed",
        props: {
          provider: "STRIPE",
          reason: "Error during invoice payment and booking creation"
        }
      });
    });
  };
  const handleChange = (event: StripeCardElementChangeEvent) => {
    /**
     * Listen for changes in the StripeCardElement.
     * Display any detected errors as the customer
     * types their card details.
     */
    setError(event.error ? event.error.message : "");
    setInvalidCard(!!event.error);
  };
  return <Form id="payment-form" onSubmit={handleSubmit} data-sentry-element="Form" data-sentry-component="StripePaymentForm" data-sentry-source-file="StripePaymentForm.tsx">
      <StripeCardElementInput id="card-element" aria-label={t("stripe.cardInputAriaLabel")} options={cardStyle} onChange={handleChange} data-sentry-element="StripeCardElementInput" data-sentry-source-file="StripePaymentForm.tsx" />
      {error && <ErrorNotification variant="warning" role="alert">
          {error}
        </ErrorNotification>}
      <ButtonContainer data-sentry-element="ButtonContainer" data-sentry-source-file="StripePaymentForm.tsx">
        <Button variant="secondary" onClick={onGoBack} data-sentry-element="Button" data-sentry-source-file="StripePaymentForm.tsx">
          {t("back")}
        </Button>
        <Button disabled={processing || succeeded} id="submit" variant="primary" onClick={handleSubmit} data-sentry-element="Button" data-sentry-source-file="StripePaymentForm.tsx">
          {processing ? <LoadingSpinner colour={theme.colours.textOnPrimary} /> : confirmPaymentLabel}
        </Button>
      </ButtonContainer>
    </Form>;
}

/**
 * Defines the properties on the configurable CardElement
 * component provided by stripe.
 *
 * https://stripe.com/docs/js/appendix/style
 */
const cardStyle = {
  hidePostalCode: true,
  style: {
    base: {
      color: "#000000",
      fontFamily: "Lato, sans-serif",
      fontSize: "16px",
      fontWeight: "900",
      lineHeight: "32px"
    },
    invalid: {
      color: "#ab0050",
      iconColor: "#ab0050"
    }
  }
};
const StripeCardElementInput = styled(CardElement)`
  display: flex;
  border: 1px solid black;
  height: auto;
  flex-direction: column;

  ${padding.m}
`;
const Form = styled.form`
  display: flex;
  flex-direction: column;
`;
const ButtonContainer = styled.nav`
  width: 100%;
  display: flex;
  ${marginTop.l}
  justify-content: space-between;

  @media ${media.mobile} {
    flex-direction: column-reverse;
    height: 6rem;
  }
`;
const Button = styled(BaseButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 10rem;

  @media ${media.mobile} {
    min-width: 100%;
  }
`;
const ErrorNotification = styled(Notification)`
  ${marginTop.m}
`;